import * as THREE from "../../../libs/three.js/build/three.module.js";
import {collectLoadedGeometryNodes} from "../LER/LERParser.js";

// 3D-baggrundskort: terraenet + ortofoto som et tile-lag (quadtree) rundt om
// kameraet, til de omraader hvor der ikke er punktskyer.
//
// - Motoren ejer kun geometri, LOD, indlaesning og hul-maske. Selve DATAENE leveres
//   af en "provider" (se nedenfor), saa potree hverken kender en tjeneste, en
//   noegle eller et filformat (frontenden henter DHM + ortofoto fra Datafordeleren).
// - Tiles ligger i et fast net i scenens koordinater (absolut EPSG:25832, som
//   GIS-laget): niveau L har kantlaengde ROOT_SIZE / 2^L. Et tile deles i fire,
//   naar kameraet er taettere paa end REFINE_FACTOR * tilens kantlaengde; foerst
//   naar alle fire boern er indlaest erstatter de forælderen (ingen huller).
//   Kanter mellem forskellige niveauer skjules af nederdele (skirts).
// - Hul-maske: de omraader hvor der allerede er punktskyer klippes vaek af
//   baggrundskortet. Masken tegnes ud fra punktskyernes EGNE punkter (de groveste
//   indlaeste noder) udvidet med en buffer, saa den foelger den rigtige daekning
//   uanset form, og den opdateres af sig selv, naar punktskyer kommer/gaar.
//
// provider: {
//   fetchTile(bbox: {minX, minY, maxX, maxY}, {vertices, texturePx}) ->
//     Promise<{heights: Float32Array(vertices*vertices), image: CanvasImageSource}>
//   heights er raekkefoelge syd -> nord, vest -> oest, og vertex (r, c) ligger
//   praecis i (minX + c/(vertices-1)*bboxbredde, minY + r/(vertices-1)*bboxhoejde).
//   image daekker bbox'en (nord = oeverst).
// }

const ROOT_SIZE = 16384;
const VERTICES = 33;
const TEXTURE_PX = 256;

const DEFAULTS = {
	minLevel: 4,          // 1 km-tiles
	maxLevel: 9,          // 32 m-tiles (ortofotoet er ca. 12,5 cm/pixel)
	radius: 3000,         // m om kameraet
	coverageBuffer: null, // tal (m): vis KUN kortet inden for den afstand af punktskyernes daekning
	refineFactor: 2.0,
	concurrency: 6,
	maxTiles: 600,
	pickable: true,       // klik/dobbeltklik/scroll i baggrundskortet virker i navigationen
	holeBuffer: 0,        // m ekstra om punktskyernes punkter
	maskMaxPx: 4096,
	updateIntervalMs: 150,
	maskIntervalMs: 1000,
	retryDelayMs: 5000,
};

class TileNode{
	constructor(level, ix, iy){
		this.level = level;
		this.ix = ix;
		this.iy = iy;
		this.size = ROOT_SIZE / Math.pow(2, level);
		this.minX = ix * this.size;
		this.minY = iy * this.size;
		this.maxX = this.minX + this.size;
		this.maxY = this.minY + this.size;
		this.key = `${level}/${ix}/${iy}`;
		this.state = "empty"; // empty | loading | loaded | failed
		this.mesh = null;
		this.children = null;
		this.lastUsed = 0;
		this.retryAt = 0;
		this.zMin = 0;
		this.zMax = 0;
		this.maskOffset = {value: new THREE.Vector2()};
	}

	get loaded(){
		return this.state === "loaded";
	}

	// 2D-afstand fra et punkt til tilens rektangel (0 inde i den)
	distance2D(x, y){
		let dx = Math.max(this.minX - x, 0, x - this.maxX);
		let dy = Math.max(this.minY - y, 0, y - this.maxY);
		return Math.sqrt(dx * dx + dy * dy);
	}
}

export class BaseMap3D extends THREE.Object3D{

	constructor(viewer, provider, options = {}){
		super();

		this.name = "basemap_3d";
		// IKKE-enumerable: laget haenger i scenegrafen (viewer.scene.scene), og PointView (Vue 2) kan komme til at
		// observere hele grafen (fx via GIS-lagets parent). Vue gaar kun igennem enumerable egenskaber - ellers naar
		// det via .viewer helt ind i profilvinduets d3-selektioner og erstatter deres prototype ("elXAxis.attr is not a function").
		Object.defineProperty(this, "viewer", {value: viewer, writable: true, enumerable: false, configurable: true});
		Object.defineProperty(this, "provider", {value: provider, writable: true, enumerable: false, configurable: true});
		this.options = Object.assign({}, DEFAULTS, options);

		this.tiles = new Map();
		this.active = 0;
		this.frame = 0;
		this.dirty = true;
		this.lastUpdate = 0;
		this.lastMaskCheck = 0;
		this.maskSignature = "";
		this.opacity = 1;
		this.zOffset = 0;
		this.zRef = 0;
		this.errors = 0;
		this.lastError = null;
		this.disposed = false;
		this.onStatus = null;

		// delte uniforms (samme objekter i alle tiles' materialer)
		this.maskTexture = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
		this.maskTexture.needsUpdate = true;
		this.maskUniforms = {
			holeMap: {value: this.maskTexture},
			holeSize: {value: new THREE.Vector2(1, 1)},
			holeOn: {value: 0},
			keepOn: {value: 0},
		};
		this.pickMask = null;  // lille CPU-kopi af masken, til at afgoere hvad der er synligt ved klik
		this.pickMeshes = [];  // de tiles der lige nu staar i viewer.scene.pickableMeshes
		this.keepGrid = null; // lavopløst gitter over "behold"-omraadet (coverageBuffer)
		this.keepVersion = 0;
		this.maskBox = null; // {minX, maxY, sizeX, sizeY}

		this.heightTiles = new Map(); // dataonly-cache til sampleHeights (profilet)

		this.updateListener = () => this.update();
		viewer.addEventListener("update", this.updateListener);
		viewer.scene.scene.add(this);
		viewer.baseMap3D = this;
		viewer.dispatchEvent({type: "profile_overlays_dirty"});
	}

	// --- offentlig API ------------------------------------------------------

	// Viser/skjuler baggrundskortet uden at fjerne det (Registreringer-trædets øje): skjult tegnes og opdateres det ikke, og
	// fliserne tages ud af klik-/snap-listen (viewer.scene.pickableMeshes), så man ikke klikker på et usynligt kort.
	setVisible(value){
		value = !!value;
		if(this.visible === value) return;
		this.visible = value;
		if(!value){
			this.syncPickable([]);
		}
		this.viewer.dispatchEvent({type: "profile_overlays_dirty"});
		if(typeof this.viewer._requestRedraw === "function") this.viewer._requestRedraw(); else this.viewer.needsRedraw = true;
	}

	setOpacity(value){
		this.opacity = value;
		for(let tile of this.tiles.values()){
			if(tile.mesh){
				this.applyOpacity(tile.mesh.material);
			}
		}
	}

	setZOffset(value){
		this.zOffset = value;
		for(let tile of this.tiles.values()){
			if(tile.mesh){
				tile.mesh.position.z = tile.zBase + this.zOffset;
				tile.mesh.updateMatrixWorld(true);
			}
		}
	}

	setRadius(value){
		this.options.radius = value;
		this.dirty = true;
	}

	// null = baggrundskort rundt om kameraet (radius); tal = kun inden for den afstand (m)
	// af punktskyernes daekning
	setCoverageBuffer(value){
		this.options.coverageBuffer = value;
		this.maskSignature = "";
		this.dirty = true;
		this.viewer.dispatchEvent({type: "profile_overlays_dirty"});
	}

	// Terraenhoejder i en liste af {x, y} (til profilvinduets terraenlinje). Henter manglende
	// hoejdegitre (33x33, 64 m-tiles) uden at bygge meshes; uden for "behold"-zonen i
	// coverageBuffer-tilstand (og hvor der ingen data er) bliver hoejden NaN, saa linjen brydes.
	async sampleHeights(samples){
		const LEVEL = 8;
		const size = ROOT_SIZE / Math.pow(2, LEVEL);
		let n = VERTICES;

		let keys = new Map();
		for(let s of samples){
			let ix = Math.floor(s.x / size);
			let iy = Math.floor(s.y / size);
			let key = `${LEVEL}/${ix}/${iy}`;
			if(!keys.has(key)){
				keys.set(key, {ix, iy});
			}
		}

		let missing = [...keys.entries()].filter(([key]) => !this.heightTiles.has(key));
		if(missing.length > 600){
			return new Float64Array(samples.length).fill(NaN); // for stor strækning
		}

		let next = 0;
		let worker = async () => {
			while(next < missing.length){
				let [key, {ix, iy}] = missing[next++];
				let bbox = {minX: ix * size, minY: iy * size, maxX: (ix + 1) * size, maxY: (iy + 1) * size};
				try{
					let heights;
					if(this.provider.fetchHeights){
						heights = await this.provider.fetchHeights(bbox, n);
					}else{
						heights = (await this.provider.fetchTile(bbox, {vertices: n, texturePx: 16})).heights;
					}
					this.heightTiles.set(key, heights);
				}catch(e){
					this.heightTiles.set(key, null);
				}
			}
		};
		await Promise.all(Array.from({length: Math.min(this.options.concurrency, missing.length)}, worker));

		let out = new Float64Array(samples.length);
		for(let k = 0; k < samples.length; k++){
			let s = samples[k];
			let ix = Math.floor(s.x / size);
			let iy = Math.floor(s.y / size);
			let heights = this.heightTiles.get(`${LEVEL}/${ix}/${iy}`);
			if(!heights || !this.inKeepZone(s.x, s.y)){
				out[k] = NaN;
				continue;
			}
			let gx = ((s.x - ix * size) / size) * (n - 1);
			let gy = ((s.y - iy * size) / size) * (n - 1);
			let c0 = Math.min(n - 2, Math.max(0, Math.floor(gx)));
			let r0 = Math.min(n - 2, Math.max(0, Math.floor(gy)));
			let fx = gx - c0;
			let fy = gy - r0;
			let h00 = heights[r0 * n + c0];
			let h10 = heights[r0 * n + c0 + 1];
			let h01 = heights[(r0 + 1) * n + c0];
			let h11 = heights[(r0 + 1) * n + c0 + 1];
			out[k] = (h00 * (1 - fx) + h10 * fx) * (1 - fy) + (h01 * (1 - fx) + h11 * fx) * fy;
		}
		return out;
	}

	// Ligger (x, y) i den zone hvor baggrundskortet vises (hullet i punktskyen tæller med)?
	inKeepZone(x, y){
		if(this.options.coverageBuffer == null){
			return true;
		}
		let m = this.pickMask;
		if(!m){
			return false;
		}
		let u = (x - m.minX) / m.sizeX;
		let v = (m.maxY - y) / m.sizeY;
		if(u < 0 || u > 1 || v < 0 || v > 1){
			return false;
		}
		let i = Math.min(m.w - 1, Math.floor(u * m.w));
		let j = Math.min(m.h - 1, Math.floor(v * m.h));
		return m.data[(j * m.w + i) * 4 + 1] >= 128;
	}

	setHoleBuffer(value){
		this.options.holeBuffer = value;
		this.maskSignature = ""; // tving ny maske
	}

	getStats(){
		let loaded = 0;
		let drawn = 0;
		for(let tile of this.tiles.values()){
			if(tile.loaded){
				loaded++;
			}
			if(tile.mesh && tile.mesh.visible){
				drawn++;
			}
		}
		return {loaded, drawn, loading: this.active, errors: this.errors, lastError: this.lastError, hasCoverage: !!this.keepGrid, coverageMode: this.options.coverageBuffer != null};
	}

	dispose(){
		this.disposed = true;
		this.syncPickable([]);
		if(this.viewer.baseMap3D === this){
			this.viewer.baseMap3D = null;
			this.viewer.dispatchEvent({type: "profile_overlays_dirty"});
		}
		this.viewer.removeEventListener("update", this.updateListener);
		this.viewer.scene.scene.remove(this);
		for(let tile of this.tiles.values()){
			this.disposeTile(tile);
		}
		this.tiles.clear();
		this.maskTexture.dispose();
	}

	// --- opdatering pr. billede ---------------------------------------------

	update(){
		if(this.disposed || !this.visible){
			return;
		}

		let now = performance.now();
		if(now - this.lastMaskCheck > this.options.maskIntervalMs){
			this.lastMaskCheck = now;
			this.updateMask();
		}

		let camera = this.viewer.scene.getActiveCamera();
		let position = camera.position;
		if(!this.lastCamera || !this.lastCamera.equals(position)){
			this.lastCamera = position.clone();
			this.dirty = true;
		}

		// uden kamerabevaegelse genvurderes alligevel en gang i sekundet (genforsoeg af fejlede tiles)
		if(now - this.lastUpdate > 1000){
			this.dirty = true;
		}

		if(!this.dirty || now - this.lastUpdate < this.options.updateIntervalMs){
			return;
		}
		this.dirty = false;
		this.lastUpdate = now;
		this.frame++;

		let wanted = new Map();
		let drawn = new Set();
		this.select(position, wanted, drawn);

		for(let tile of this.tiles.values()){
			if(tile.mesh){
				tile.mesh.visible = drawn.has(tile);
			}
		}

		this.syncPickable(this.options.pickable ? [...drawn].map(tile => tile.mesh) : []);
		this.startLoads(wanted);
		this.evict(drawn);
	}

	// Holder viewer.scene.pickableMeshes i takt med de tiles der tegnes (andre modulers
	// meshes roeres ikke). Navigationen (klik for omdrejningspunkt, dobbeltklik, scroll mod
	// markoeren) og maalevaerktoejerne rammer dermed ogsaa baggrundskortet.
	syncPickable(meshes){
		let same = meshes.length === this.pickMeshes.length && meshes.every((m, i) => m === this.pickMeshes[i]);
		if(same){
			return;
		}

		let scene = this.viewer.scene;
		let others = (scene.pickableMeshes || []).filter(m => !this.pickMeshes.includes(m));
		this.pickMeshes = meshes;
		scene.pickableMeshes = others.concat(meshes);
	}

	// Er baggrundskortet synligt i (x, y)? Det er det ikke inde i et punktsky-hul, og i
	// coverageBuffer-tilstand heller ikke uden for zonen (shaderen kasserer fragmenterne dér,
	// men en straale rammer stadig geometrien) - klik skal ramme det man kan se.
	visibleAt(x, y){
		let m = this.pickMask;
		if(!m){
			return this.options.coverageBuffer == null;
		}

		let u = (x - m.minX) / m.sizeX;
		let v = (m.maxY - y) / m.sizeY;
		if(u < 0 || u > 1 || v < 0 || v > 1){
			return !m.coverage;
		}

		let i = Math.min(m.w - 1, Math.floor(u * m.w));
		let j = Math.min(m.h - 1, Math.floor(v * m.h));
		let k = (j * m.w + i) * 4;
		if(m.data[k] > 127){
			return false;
		}
		if(m.coverage && m.data[k + 1] < 128){
			return false;
		}
		return true;
	}

	select(cameraPosition, wanted, drawn){
		let o = this.options;
		let cx = cameraPosition.x;
		let cy = cameraPosition.y;
		let height = Math.abs(cameraPosition.z - this.zRef);

		let distance = (tile) => {
			let d2 = tile.distance2D(cx, cy);
			return {d2, d3: Math.sqrt(d2 * d2 + height * height)};
		};

		let want = (tile, d) => {
			if(tile.state === "empty" || (tile.state === "failed" && performance.now() > tile.retryAt)){
				wanted.set(tile.key, {tile, d});
			}
		};

		let coverage = o.coverageBuffer != null;
		if(coverage && !this.keepGrid){
			return; // ingen punktskyer -> intet at vise
		}
		let reach = coverage ? Infinity : o.radius;

		let visit = (tile) => {
			let {d2, d3} = distance(tile);
			if(d2 > reach + tile.size || !this.keepIntersects(tile)){
				return;
			}
			tile.lastUsed = this.frame;

			if(!tile.loaded){
				want(tile, d3);
				return;
			}

			if(tile.level < o.maxLevel && d3 < o.refineFactor * tile.size){
				let kids = this.childrenOf(tile).filter(c => c.distance2D(cx, cy) <= reach + c.size && this.keepIntersects(c));
				let ready = kids.every(c => c.loaded);
				if(ready){
					for(let kid of kids){
						visit(kid);
					}
					return;
				}
				for(let kid of kids){
					kid.lastUsed = this.frame;
					if(!kid.loaded){
						want(kid, distance(kid).d3);
					}
				}
			}

			drawn.add(tile);
		};

		let size = ROOT_SIZE / Math.pow(2, o.minLevel);
		let area = coverage
			? {minX: this.keepGrid.minX, maxX: this.keepGrid.minX + this.keepGrid.w * this.keepGrid.px, minY: this.keepGrid.maxY - this.keepGrid.h * this.keepGrid.px, maxY: this.keepGrid.maxY}
			: {minX: cx - o.radius, maxX: cx + o.radius, minY: cy - o.radius, maxY: cy + o.radius};
		let x0 = Math.floor(area.minX / size);
		let x1 = Math.floor(area.maxX / size);
		let y0 = Math.floor(area.minY / size);
		let y1 = Math.floor(area.maxY / size);
		for(let ix = x0; ix <= x1; ix++){
			for(let iy = y0; iy <= y1; iy++){
				visit(this.getTile(o.minLevel, ix, iy));
			}
		}
	}

	getTile(level, ix, iy){
		let key = `${level}/${ix}/${iy}`;
		let tile = this.tiles.get(key);
		if(!tile){
			tile = new TileNode(level, ix, iy);
			this.tiles.set(key, tile);
		}
		return tile;
	}

	childrenOf(tile){
		if(!tile.children){
			tile.children = [];
			for(let dy = 0; dy < 2; dy++){
				for(let dx = 0; dx < 2; dx++){
					tile.children.push(this.getTile(tile.level + 1, tile.ix * 2 + dx, tile.iy * 2 + dy));
				}
			}
		}
		return tile.children;
	}

	// --- indlaesning --------------------------------------------------------

	startLoads(wanted){
		let list = [...wanted.values()].sort((a, b) => a.d - b.d);
		for(let {tile} of list){
			if(this.active >= this.options.concurrency){
				break;
			}
			this.loadTile(tile);
		}
	}

	async loadTile(tile){
		tile.state = "loading";
		this.active++;
		try{
			let bbox = {minX: tile.minX, minY: tile.minY, maxX: tile.maxX, maxY: tile.maxY};
			let data = await this.provider.fetchTile(bbox, {vertices: VERTICES, texturePx: TEXTURE_PX});
			if(this.disposed){
				return;
			}
			this.buildTile(tile, data);
			tile.state = "loaded";
			this.lastError = null;
		}catch(e){
			tile.state = "failed";
			tile.retryAt = performance.now() + this.options.retryDelayMs;
			this.errors++;
			this.lastError = e && e.message ? e.message : String(e);
		}finally{
			this.active--;
			this.dirty = true;
			if(this.onStatus){
				this.onStatus(this.getStats());
			}
		}
	}

	buildTile(tile, {heights, image}){
		let n = VERTICES;
		let size = tile.size;
		let zMin = Infinity;
		let zMax = -Infinity;
		for(let i = 0; i < heights.length; i++){
			let z = heights[i];
			if(Number.isFinite(z)){
				if(z < zMin){ zMin = z; }
				if(z > zMax){ zMax = z; }
			}
		}
		if(zMin === Infinity){
			zMin = 0;
			zMax = 0;
		}
		let fallback = zMin;

		// hovedgitter + nederdel langs de fire kanter (n*n + 4*n ekstra hjoerner)
		let mainCount = n * n;
		let skirtCount = 4 * n;
		let positions = new Float32Array((mainCount + skirtCount) * 3);
		let uvs = new Float32Array((mainCount + skirtCount) * 2);
		let skirtDepth = Math.max(1.5, size * 0.02);

		for(let r = 0; r < n; r++){
			for(let c = 0; c < n; c++){
				let i = r * n + c;
				let z = heights[i];
				positions[i * 3 + 0] = (c / (n - 1) - 0.5) * size;
				positions[i * 3 + 1] = (r / (n - 1) - 0.5) * size;
				positions[i * 3 + 2] = (Number.isFinite(z) ? z : fallback) - zMin;
				uvs[i * 2 + 0] = c / (n - 1);
				uvs[i * 2 + 1] = r / (n - 1);
			}
		}

		let edge = [];
		for(let c = 0; c < n; c++){ edge.push(c); }                       // syd
		for(let r = 0; r < n; r++){ edge.push(r * n + (n - 1)); }         // oest
		for(let c = n - 1; c >= 0; c--){ edge.push((n - 1) * n + c); }    // nord
		for(let r = n - 1; r >= 0; r--){ edge.push(r * n); }              // vest
		// edge har 4*n indeks (hjoerner gentages, uskadeligt)
		for(let k = 0; k < edge.length; k++){
			let src = edge[k];
			let dst = mainCount + k;
			positions[dst * 3 + 0] = positions[src * 3 + 0];
			positions[dst * 3 + 1] = positions[src * 3 + 1];
			positions[dst * 3 + 2] = positions[src * 3 + 2] - skirtDepth;
			uvs[dst * 2 + 0] = uvs[src * 2 + 0];
			uvs[dst * 2 + 1] = uvs[src * 2 + 1];
		}

		let indices = new Uint32Array((n - 1) * (n - 1) * 6 + (edge.length - 1) * 6);
		let p = 0;
		for(let r = 0; r < n - 1; r++){
			for(let c = 0; c < n - 1; c++){
				let a = r * n + c;
				let b = a + 1;
				let d = a + n;
				let e = d + 1;
				indices[p++] = a; indices[p++] = b; indices[p++] = d;
				indices[p++] = b; indices[p++] = e; indices[p++] = d;
			}
		}
		for(let k = 0; k < edge.length - 1; k++){
			let a = edge[k];
			let b = edge[k + 1];
			if(a === b){
				continue;
			}
			let a2 = mainCount + k;
			let b2 = mainCount + k + 1;
			indices[p++] = a; indices[p++] = b; indices[p++] = a2;
			indices[p++] = b; indices[p++] = b2; indices[p++] = a2;
		}

		let geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
		geometry.setIndex(new THREE.BufferAttribute(indices.subarray(0, p), 1));
		geometry.computeBoundingSphere();

		let texture = new THREE.Texture(image);
		texture.anisotropy = 4;
		texture.generateMipmaps = true;
		texture.minFilter = THREE.LinearMipmapLinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.needsUpdate = true;

		let material = new THREE.MeshBasicMaterial({
			map: texture,
			side: THREE.DoubleSide,
			polygonOffset: true,
			polygonOffsetFactor: 2,
			polygonOffsetUnits: 2,
		});
		this.patchMaterial(material, tile);
		this.applyOpacity(material);

		let mesh = new THREE.Mesh(geometry, material);
		mesh.name = `basemap_${tile.key}`;
		mesh.visible = false;
		mesh.userData.noSnap = true; // ingen hjoerne-/kant-snapping paa terraennet
		mesh.userData.basemapTile = true;
		mesh.raycast = (raycaster, intersects) => {
			let hits = [];
			THREE.Mesh.prototype.raycast.call(mesh, raycaster, hits);
			for(let hit of hits){
				if(this.visibleAt(hit.point.x, hit.point.y)){
					intersects.push(hit);
				}
			}
		};
		tile.zBase = zMin;
		tile.zMin = zMin;
		tile.zMax = zMax;
		mesh.position.set((tile.minX + tile.maxX) / 2, (tile.minY + tile.maxY) / 2, zMin + this.zOffset);
		mesh.updateMatrixWorld(true);
		tile.mesh = mesh;
		this.add(mesh);
		this.updateMaskOffset(tile);

		// groft hoejdeoverslag til afstandsberegningen
		if(tile.level <= this.options.minLevel){
			this.zRef = (zMin + zMax) / 2;
		}
	}

	applyOpacity(material){
		material.opacity = this.opacity;
		material.transparent = this.opacity < 1;
		material.depthWrite = this.opacity >= 1;
		material.needsUpdate = true;
	}

	// Hul-masken: kasserer fragmenter inde i punktskyernes daekning. Verdens-xy
	// beregnes ud fra tilens lokale position + et forskydnings-uniform udregnet
	// i float64 paa CPU'en (absolutte koordinater i float32 er kun nøjagtige til ca. 0,5 m).
	patchMaterial(material, tile){
		let u = this.maskUniforms;
		material.onBeforeCompile = (shader) => {
			shader.uniforms.holeMap = u.holeMap;
			shader.uniforms.holeSize = u.holeSize;
			shader.uniforms.holeOn = u.holeOn;
			shader.uniforms.keepOn = u.keepOn;
			shader.uniforms.tileMaskOffset = tile.maskOffset;

			shader.vertexShader = shader.vertexShader
				.replace("#include <common>", "#include <common>\nuniform vec2 tileMaskOffset;\nvarying vec2 vHoleXY;")
				.replace("#include <begin_vertex>", "#include <begin_vertex>\nvHoleXY = vec2(tileMaskOffset.x + position.x, tileMaskOffset.y - position.y);");

			shader.fragmentShader = shader.fragmentShader
				.replace("#include <common>", "#include <common>\nuniform sampler2D holeMap;\nuniform vec2 holeSize;\nuniform float holeOn;\nuniform float keepOn;\nvarying vec2 vHoleXY;")
				.replace("#include <map_fragment>", "#include <map_fragment>\nif(holeOn > 0.5){ vec2 huv = vHoleXY / holeSize; vec4 hm = texture2D(holeMap, clamp(huv, 0.0, 1.0)); if(hm.r > 0.5){ discard; } if(keepOn > 0.5 && (hm.g < 0.5 || huv.x < 0.0 || huv.x > 1.0 || huv.y < 0.0 || huv.y > 1.0)){ discard; } }");
		};
	}

	updateMaskOffset(tile){
		if(!this.maskBox){
			return;
		}
		let cx = (tile.minX + tile.maxX) / 2;
		let cy = (tile.minY + tile.maxY) / 2;
		tile.maskOffset.value.set(cx - this.maskBox.minX, this.maskBox.maxY - cy);
	}

	// --- hul-maske ----------------------------------------------------------

	updateMask(){
		let pointclouds = this.viewer.scene.pointclouds || [];

		// signatur: hvilke punktskyer, og hvor mange grove noder der er indlaest
		let nodesPerCloud = pointclouds.map(pc => this.coarseNodes(pc));
		let signature = pointclouds.map((pc, i) => `${pc.name}:${nodesPerCloud[i].length}`).join("|") + `|${this.options.holeBuffer}|${this.options.coverageBuffer}`;
		if(signature === this.maskSignature){
			return;
		}
		this.maskSignature = signature;

		let xs = [];
		let ys = [];
		let radii = [];
		let total = 0;
		for(let i = 0; i < pointclouds.length; i++){
			total += nodesPerCloud[i].reduce((sum, node) => sum + node.geometry.attributes.position.count, 0);
		}
		let stride = Math.max(1, Math.ceil(total / 1500000));

		for(let i = 0; i < pointclouds.length; i++){
			let pc = pointclouds[i];
			pc.updateMatrixWorld(true);
			let spacing = (pc.pcoGeometry && pc.pcoGeometry.spacing) || 1;
			// noder paa grov niveau er sparsomme: udvid med et par gange punktafstanden
			let radius = Math.min(15, Math.max(1, spacing * 1.2)) + this.options.holeBuffer;
			for(let node of nodesPerCloud[i]){
				let min = node.boundingBox.min;
				let matrix = new THREE.Matrix4().multiplyMatrices(pc.matrixWorld, new THREE.Matrix4().makeTranslation(min.x, min.y, min.z));
				let e = matrix.elements;
				let array = node.geometry.attributes.position.array;
				for(let k = 0; k < array.length; k += 3 * stride){
					let px = array[k], py = array[k + 1], pz = array[k + 2];
					xs.push(e[0] * px + e[4] * py + e[8] * pz + e[12]);
					ys.push(e[1] * px + e[5] * py + e[9] * pz + e[13]);
					radii.push(radius);
				}
			}
		}

		if(xs.length === 0){
			this.maskUniforms.holeOn.value = 0;
			this.maskUniforms.keepOn.value = 0;
			this.maskBox = null;
			this.keepGrid = null;
			this.pickMask = null;
			this.keepVersion++;
			this.dirty = true;
			if(this.onStatus){
				this.onStatus(this.getStats());
			}
			return;
		}

		let coverage = this.options.coverageBuffer != null;
		let keepBuffer = coverage ? this.options.coverageBuffer : 0;

		let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, maxR = 0;
		for(let k = 0; k < xs.length; k++){
			minX = Math.min(minX, xs[k]); maxX = Math.max(maxX, xs[k]);
			minY = Math.min(minY, ys[k]); maxY = Math.max(maxY, ys[k]);
			maxR = Math.max(maxR, radii[k]);
		}
		let pad = Math.max(maxR, keepBuffer) + 1;
		minX -= pad; minY -= pad; maxX += pad; maxY += pad;

		let sizeX = maxX - minX;
		let sizeY = maxY - minY;
		let pxSize = Math.max(0.25, Math.max(sizeX, sizeY) / this.options.maskMaxPx);
		let width = Math.max(2, Math.ceil(sizeX / pxSize));
		let height = Math.max(2, Math.ceil(sizeY / pxSize));

		let canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		let ctx = canvas.getContext("2d");
		ctx.fillStyle = "#000";
		ctx.fillRect(0, 0, width, height);

		// "behold"-omraadet (groen kanal): punktskyernes daekning udvidet med en cirkel paa
		// keepBuffer, tegnet paa et grovt gitter (punkterne samles foerst i celler)
		this.keepGrid = null;
		if(coverage){
			let lowTarget = Math.max(4, Math.max(sizeX, sizeY) / 2048);
			let lw = Math.max(2, Math.ceil(sizeX / lowTarget));
			let lh = Math.max(2, Math.ceil(sizeY / lowTarget));
			let lpxX = (width * pxSize) / lw;
			let lpxY = (height * pxSize) / lh;
			let low = document.createElement("canvas");
			low.width = lw;
			low.height = lh;
			let lctx = low.getContext("2d");
			lctx.fillStyle = "#000";
			lctx.fillRect(0, 0, lw, lh);
			let occupied = new Uint8Array(lw * lh);
			lctx.fillStyle = "#0f0";
			lctx.beginPath();
			let rx = keepBuffer / lpxX;
			for(let k = 0; k < xs.length; k++){
				let cx = Math.min(lw - 1, Math.max(0, Math.floor((xs[k] - minX) / lpxX)));
				let cy = Math.min(lh - 1, Math.max(0, Math.floor((maxY - ys[k]) / lpxY)));
				if(occupied[cy * lw + cx]){
					continue;
				}
				occupied[cy * lw + cx] = 1;
				lctx.moveTo(cx + 0.5 + rx, cy + 0.5);
				lctx.ellipse(cx + 0.5, cy + 0.5, rx, keepBuffer / lpxY, 0, 0, Math.PI * 2);
			}
			lctx.fill();
			let image = lctx.getImageData(0, 0, lw, lh).data;
			let data = new Uint8Array(lw * lh);
			for(let k = 0; k < data.length; k++){
				data[k] = image[k * 4 + 1] > 127 ? 1 : 0;
			}
			this.keepGrid = {data, w: lw, h: lh, px: lpxX, pyY: lpxY, minX, maxY};
			ctx.imageSmoothingEnabled = true;
			ctx.drawImage(low, 0, 0, width, height);
			ctx.globalCompositeOperation = "lighter";
		}
		ctx.fillStyle = coverage ? "#f00" : "#fff";
		for(let k = 0; k < xs.length; k++){
			let r = Math.max(1, radii[k] / pxSize);
			ctx.fillRect((xs[k] - minX) / pxSize - r, (maxY - ys[k]) / pxSize - r, 2 * r, 2 * r);
		}

		let texture = new THREE.CanvasTexture(canvas);
		texture.flipY = false;
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.generateMipmaps = false;
		texture.wrapS = THREE.ClampToEdgeWrapping;
		texture.wrapT = THREE.ClampToEdgeWrapping;
		texture.needsUpdate = true;

		this.maskBox = {minX, maxY, sizeX: width * pxSize, sizeY: height * pxSize};

		// lille CPU-kopi (maks. 1024 px) af masken til visibleAt()
		let scale = Math.min(1, 1024 / Math.max(width, height));
		let pw = Math.max(1, Math.round(width * scale));
		let ph = Math.max(1, Math.round(height * scale));
		let small = document.createElement("canvas");
		small.width = pw;
		small.height = ph;
		let sctx = small.getContext("2d");
		sctx.imageSmoothingEnabled = true;
		sctx.drawImage(canvas, 0, 0, pw, ph);
		this.pickMask = {data: sctx.getImageData(0, 0, pw, ph).data, w: pw, h: ph, minX, maxY, sizeX: width * pxSize, sizeY: height * pxSize, coverage};
		this.maskUniforms.holeSize.value.set(this.maskBox.sizeX, this.maskBox.sizeY);
		let old = this.maskTexture;
		this.maskTexture = texture;
		this.maskUniforms.holeMap.value = texture;
		this.maskUniforms.holeOn.value = 1;
		this.maskUniforms.keepOn.value = coverage ? 1 : 0;
		this.keepVersion++;
		this.dirty = true;
		old.dispose();
		this.viewer.dispatchEvent({type: "profile_overlays_dirty"});
		if(this.onStatus){
			this.onStatus(this.getStats());
		}

		for(let tile of this.tiles.values()){
			if(tile.mesh){
				this.updateMaskOffset(tile);
			}
		}
	}

	// Beroerer tilen "behold"-omraadet? (kun i coverageBuffer-tilstand; ellers altid)
	keepIntersects(tile){
		if(this.options.coverageBuffer == null){
			return true;
		}
		let grid = this.keepGrid;
		if(!grid){
			return false;
		}
		if(tile.keepVersion === this.keepVersion){
			return tile.keepHit;
		}

		let i0 = Math.max(0, Math.floor((tile.minX - grid.minX) / grid.px));
		let i1 = Math.min(grid.w - 1, Math.floor((tile.maxX - grid.minX) / grid.px));
		let j0 = Math.max(0, Math.floor((grid.maxY - tile.maxY) / grid.pyY));
		let j1 = Math.min(grid.h - 1, Math.floor((grid.maxY - tile.minY) / grid.pyY));
		let hit = false;
		for(let j = j0; j <= j1 && !hit; j++){
			let row = j * grid.w;
			for(let i = i0; i <= i1; i++){
				if(grid.data[row + i]){
					hit = true;
					break;
				}
			}
		}
		tile.keepVersion = this.keepVersion;
		tile.keepHit = hit;
		return hit;
	}

	// groft indlaeste noder (niveau <= 3) af en punktsky
	coarseNodes(pointcloud){
		let root = pointcloud.pcoGeometry && pointcloud.pcoGeometry.root;
		return collectLoadedGeometryNodes(root).filter(node => (node.level === undefined || node.level <= 3) && node.geometry.attributes.position);
	}

	// --- oprydning ----------------------------------------------------------

	evict(drawn){
		let loaded = [];
		for(let tile of this.tiles.values()){
			if(tile.mesh){
				loaded.push(tile);
			}
		}
		let excess = loaded.length - this.options.maxTiles;
		if(excess <= 0){
			return;
		}

		loaded.sort((a, b) => a.lastUsed - b.lastUsed);
		for(let tile of loaded){
			if(excess <= 0){
				break;
			}
			if(drawn.has(tile) || tile.lastUsed === this.frame){
				continue;
			}
			this.disposeTile(tile);
			tile.state = "empty";
			excess--;
		}
	}

	disposeTile(tile){
		if(!tile.mesh){
			return;
		}
		this.remove(tile.mesh);
		tile.mesh.geometry.dispose();
		if(tile.mesh.material.map){
			tile.mesh.material.map.dispose();
		}
		tile.mesh.material.dispose();
		tile.mesh = null;
	}
}
